import { Link } from "react-router-dom";
import { useTokens } from "@/design-system/hooks/useTokens";
import { useNavigationService } from "@/application/services/navigationService";
import { useSidebar } from "@/application/contexts/SidebarContext";
import { useEffect, useRef } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  HomeIcon,
  UserIcon,
  DocumentIcon,
  ClipboardIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  FolderIcon,
  ChartBarIcon,
  MegaphoneIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";

/**
 * 側邊欄組件
 * 提供應用程式的主要導航功能，支援固定和浮層模式
 */
export function Sidebar() {
  const tokens = useTokens();
  const { getFilteredNavItems, isActive } = useNavigationService();
  const { isOpen, mode, toggleSidebar, closeSidebar } = useSidebar();

  // 側邊欄元素參考
  const sidebarRef = useRef<HTMLDivElement>(null);

  // 處理點擊外部關閉側邊欄（在浮層模式下）
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        mode === "overlay" &&
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        closeSidebar();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [mode, isOpen, closeSidebar]);

  // 處理滑動手勢關閉（在浮層模式下）
  useEffect(() => {
    let touchStartX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      if (mode === "overlay" && isOpen) {
        touchStartX = e.touches[0].clientX;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (mode === "overlay" && isOpen && touchStartX > 0) {
        const currentX = e.touches[0].clientX;
        const diff = touchStartX - currentX;

        // 如果向左滑動超過 50px，關閉側邊欄
        if (diff > 50) {
          closeSidebar();
          touchStartX = 0;
        }
      }
    };

    const sidebarElement = sidebarRef.current;
    if (sidebarElement) {
      sidebarElement.addEventListener("touchstart", handleTouchStart);
      sidebarElement.addEventListener("touchmove", handleTouchMove);
    }

    return () => {
      if (sidebarElement) {
        sidebarElement.removeEventListener("touchstart", handleTouchStart);
        sidebarElement.removeEventListener("touchmove", handleTouchMove);
      }
    };
  }, [mode, isOpen, closeSidebar]);

  // 根據模式和狀態生成不同的樣式
  const sidebarClasses =
    mode === "docked"
      ? `h-[calc(100vh-4rem)] fixed top-16 left-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-10 ${
          isOpen
            ? "w-64 transition-width duration-300 ease-in-out"
            : "w-16 transition-width duration-300 ease-in-out"
        }`
      : `h-[calc(100vh-4rem)] fixed top-16 left-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 w-64 transform transition-transform duration-300 ease-in-out z-30 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`;

  // 如果是浮層模式且側邊欄開啟，添加遮罩層
  const overlayClasses =
    mode === "overlay" && isOpen
      ? "fixed inset-0 bg-black bg-opacity-50 z-20 transition-opacity duration-300 ease-in-out"
      : "hidden";

  return (
    <>
      {/* 遮罩層 - 只在浮層模式且側邊欄開啟時顯示 */}
      <div
        className={overlayClasses}
        onClick={closeSidebar}
        aria-hidden="true"
      ></div>

      {/* 側邊欄 */}
      <div
        ref={sidebarRef}
        className={sidebarClasses}
        style={{
          backgroundColor: tokens.colors.background.paper,
          borderRightColor: tokens.colors.border.light,
          boxShadow: isOpen && mode === "docked" ? tokens.shadows.md : "none",
        }}
        aria-hidden={!isOpen}
      >
        {/* 側邊欄內容 */}
        <div className="h-full flex flex-col justify-between overflow-y-auto pt-2">
          {/* 導航項目 - 只顯示主導航 */}
          <nav className="flex-1 px-2 py-4">
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {getFilteredNavItems()
                .filter((item) => item.isMainNav)
                .map((item) => (
                  <li key={item.path} className="py-1">
                    <Link
                      to={item.path}
                      className={`flex items-center h-10 p-1 rounded-md transition-all duration-300 ${isActive(item.path) ? "bg-primary-50 dark:bg-gray-700 text-primary-700 dark:text-primary-400" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                    >
                      {/* 使用圖標代表導航項目 */}
                      <div className="flex-shrink-0 w-10 h-6 flex items-center justify-center">
                        {item.icon === "HomeIcon" && (
                          <HomeIcon className="w-5 h-5" />
                        )}
                        {item.icon === "UserIcon" && (
                          <UserIcon className="w-5 h-5" />
                        )}
                        {item.icon === "DocumentIcon" && (
                          <DocumentIcon className="w-5 h-5" />
                        )}
                        {item.icon === "ClipboardIcon" && (
                          <ClipboardIcon className="w-5 h-5" />
                        )}
                        {item.icon === "GlobeAltIcon" && (
                          <GlobeAltIcon className="w-5 h-5" />
                        )}
                        {item.icon === "MagnifyingGlassIcon" && (
                          <MagnifyingGlassIcon className="w-5 h-5" />
                        )}
                        {item.icon === "FolderIcon" && (
                          <FolderIcon className="w-5 h-5" />
                        )}
                        {item.icon === "ChartBarIcon" && (
                          <ChartBarIcon className="w-5 h-5" />
                        )}
                        {item.icon === "MegaphoneIcon" && (
                          <MegaphoneIcon className="w-5 h-5" />
                        )}
                        {item.icon === "Cog6ToothIcon" && (
                          <Cog6ToothIcon className="w-5 h-5" />
                        )}
                        {!item.icon && <HomeIcon className="w-5 h-5" />}
                      </div>
                      <span
                        className={`ml-3 transition-all duration-300 ${mode === "docked" && !isOpen ? "opacity-0 w-0 overflow-hidden" : "opacity-100"}`}
                      >
                        {item.label}
                      </span>
                    </Link>
                  </li>
                ))}
            </ul>
          </nav>

          {/* 桌面版右側收起/展開按鈕 */}
          {mode === "docked" && (
            <button
              onClick={toggleSidebar}
              className={`absolute top-2 p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-all duration-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 z-10 shadow-sm`}
              style={{ right: "-15px" }}
              aria-label={isOpen ? "收起側邊欄" : "展開側邊欄"}
            >
              {isOpen ? (
                <ChevronLeftIcon className="w-5 h-5" />
              ) : (
                <ChevronRightIcon className="w-5 h-5" />
              )}
            </button>
          )}

          {/* 行動版底部收起/展開按鈕 */}
          {mode === "overlay" && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={toggleSidebar}
                className="w-full flex items-center justify-center p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                aria-label={isOpen ? "關閉側邊欄" : "開啟側邊欄"}
              >
                <XMarkIcon className="w-5 h-5" />
                <span className="ml-2">關閉選單</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
